import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
   getTouristCenterOverrides,
   createTouristCenterOverride,
   updateTouristCenterOverride,
   deleteTouristCenterOverride,
} from 'src/api/touristCenterOverrideApi';
import { showToast } from 'src/components/toast';
import { MutationParams } from './useDestination';
import { TouristCenterOverrideListResponse } from 'src/types/touristCenterOverride';

export const TouristCenterOverrideQueryKeys = {
   List: 'touristCenterOverrides.list',
};

interface GetTouristCenterOverridesParams {
   searchQuery?: string;
   page?: number;
   limit?: number;
}

export const useGetTouristCenterOverrides = (params: GetTouristCenterOverridesParams) => {
   return useQuery<TouristCenterOverrideListResponse>({
      queryKey: [TouristCenterOverrideQueryKeys.List, params],
      queryFn: async () => {
         const response = await getTouristCenterOverrides(params);
         return response.data as TouristCenterOverrideListResponse;
      },
      retry: 0,
   });
};

export const useCreateTouristCenterOverride = (callBack: MutationParams) => {
   const { onSuccessCallBack, onErrorCallBack, onMutateCallBack, onSettledCallBack } = callBack;
   const queryClient = useQueryClient();

   return useMutation({
      mutationFn: createTouristCenterOverride,
      onError: (err: any) => {
         const is409 = err.response?.status === 409;
         const message = is409
            ? (err.response?.data?.[0]?.detail ?? 'Conflict: override already exists')
            : (err.response?.data?.message ?? 'Error while adding new override');
         showToast('error', message);
         onErrorCallBack && onErrorCallBack();
      },
      onSuccess: async () => {
         await queryClient.invalidateQueries({ queryKey: [TouristCenterOverrideQueryKeys.List] });
         showToast('success', 'Successfully added override');
         onSuccessCallBack && onSuccessCallBack();
      },
      onMutate: () => onMutateCallBack && onMutateCallBack(),
      onSettled: () => onSettledCallBack && onSettledCallBack(),
   });
};

export const useUpdateTouristCenterOverride = (callBack: MutationParams) => {
   const { onSuccessCallBack, onErrorCallBack, onMutateCallBack, onSettledCallBack } = callBack;
   const queryClient = useQueryClient();

   return useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: any }) => updateTouristCenterOverride(id, payload),
      onError: (err: any) => {
         const is409 = err.response?.status === 409;
         const message = is409 ? (err.response?.data?.errors?.[0]?.detail ?? 'Conflict: override already exists') : 'Error while updating override';
         showToast('error', message);
         onErrorCallBack && onErrorCallBack();
      },
      onSuccess: async () => {
         await queryClient.invalidateQueries({ queryKey: [TouristCenterOverrideQueryKeys.List] });
         showToast('success', 'Successfully updated override');
         onSuccessCallBack && onSuccessCallBack();
      },
      onMutate: () => onMutateCallBack && onMutateCallBack(),
      onSettled: () => onSettledCallBack && onSettledCallBack(),
   });
};

export const useDeleteTouristCenterOverride = (callBack: MutationParams) => {
   const { onSuccessCallBack, onErrorCallBack, onMutateCallBack, onSettledCallBack } = callBack;
   const queryClient = useQueryClient();

   return useMutation({
      mutationFn: (overrideId: string) => deleteTouristCenterOverride(overrideId),
      onError: () => {
         showToast('error', 'Error while deleting override');
         onErrorCallBack && onErrorCallBack();
      },
      onSuccess: async () => {
         await queryClient.invalidateQueries({ queryKey: [TouristCenterOverrideQueryKeys.List] });
         showToast('success', 'Successfully deleted override');
         onSuccessCallBack && onSuccessCallBack();
      },
      onMutate: () => onMutateCallBack && onMutateCallBack(),
      onSettled: () => onSettledCallBack && onSettledCallBack(),
   });
};
